/**
 * Inspector section: review items scoped to the currently-selected
 * field. Renders error/warning/decision/unmapped items addressing
 * `(target_table, target_field)` and embeds the inline decision
 * resolution UI for any decision items.
 *
 * Counts and items come from `useReviewItems` (single source of truth);
 * decision resolution mutates via the same `/pending-decisions/resolve`
 * endpoint the deleted drawer used.
 */
import React from 'react';
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import {
  resolvePendingDecision,
  type PendingDecision,
  type ResolveAction,
  type ResolvePendingDecisionRequest,
} from '../../services/mappingApi';
import type { ReviewItem } from '../hooks/useReviewItems';
import { SeverityIcon } from './decisions/components';
import { FieldDecisionActions } from './FieldDecisionActions';

export interface FieldReviewItemsProps {
  batchId: string;
  items: ReviewItem[];
  /** Disable resolve actions when the recipe is in a non-editable lifecycle state. */
  resolveDisabled?: boolean;
  onSelectInlineEdit?: (target_table: string, target_field: string) => void;
  /**
   * Rule names already overridden on this field — passed through to
   * each decision so `accept_override` can be hidden where it would
   * be redundant (plan §G/13).
   */
  overriddenRuleNames?: Set<string>;
}

export const FieldReviewItems: React.FC<FieldReviewItemsProps> = ({
  batchId,
  items,
  resolveDisabled = false,
  onSelectInlineEdit,
  overriddenRuleNames,
}) => {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (request: ResolvePendingDecisionRequest) => resolvePendingDecision(batchId, request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pending-decisions', batchId]);
        queryClient.invalidateQueries(['mappings', batchId]);
        queryClient.invalidateQueries(['mapping-health', batchId]);
      },
    },
  );

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        No issues for this field.
      </Typography>
    );
  }

  const decisionItems = items.filter((it) => it.bucket === 'decision');
  const otherItems = items.filter((it) => it.bucket !== 'decision');

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {mutation.isLoading ? <LinearProgress /> : null}
      {mutation.error ? (
        <Alert severity="error">
          Resolve failed:{' '}
          {(mutation.error as any)?.response?.data?.detail ?? 'Unknown error'}
        </Alert>
      ) : null}

      {/* Non-decision items: brief rows with severity + message + bucket chip. */}
      {otherItems.map((it) => (
        <Box
          key={it.id}
          sx={{
            p: 1,
            border: '1px solid rgba(0,0,0,0.08)',
            borderLeft: (theme) =>
              `3px solid ${
                it.severity === 'error'
                  ? theme.palette.error.main
                  : it.severity === 'warning'
                    ? theme.palette.warning.main
                    : theme.palette.info.main
              }`,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            cursor:
              it.primary_action === 'open_inline_edit' ||
              it.primary_action === 're_source'
                ? 'pointer'
                : 'default',
          }}
          onClick={() => {
            if (
              (it.primary_action === 'open_inline_edit' ||
                it.primary_action === 're_source') &&
              it.target_field &&
              onSelectInlineEdit
            ) {
              onSelectInlineEdit(it.target_table, it.target_field);
            }
          }}
        >
          <SeverityIcon severity={it.severity} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2">{it.message}</Typography>
            {(it.source_field || it.operation) && (
              <Typography variant="caption" color="text.secondary">
                {it.source_field ? `source: ${it.source_file ?? '?'}.${it.source_field}` : null}
                {it.source_field && it.operation ? ' · ' : null}
                {it.operation ? `op: ${it.operation}` : null}
              </Typography>
            )}
          </Box>
          <Chip
            label={it.bucket}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem' }}
          />
        </Box>
      ))}

      {/* Decision items: full kind-aware action UI. */}
      {decisionItems.map((it) => (
        <FieldDecisionActions
          key={it.id}
          decision={it.payload as PendingDecision}
          disabled={resolveDisabled || mutation.isLoading}
          overriddenRuleNames={overriddenRuleNames}
          orphanTarget={it.orphan_target}
          onResolve={(action: ResolveAction) =>
            mutation.mutate({
              target_table: it.target_table,
              target_field: it.target_field!,
              action,
            })
          }
        />
      ))}
    </Box>
  );
};

export default FieldReviewItems;
